import { components } from "@/api-schema";
import { Action, PathsV1UserSearchPostParametersQuerySearchField, UserStatus, UserType } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { useUserActions } from "@/common/hooks/user/use-user-actions";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { UserTable } from "@/components/data-table/predefined/user-table";
import { DataTableHeightMode, DataTableRow } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { DepartmentFilter } from "@/components/filter/predefined/department-filter";
import { JobFunctionFilter } from "@/components/filter/predefined/job-function-filter";
import { OccupationFilter } from "@/components/filter/predefined/occupation-filter";
import { UserStatusFilter } from "@/components/filter/predefined/user-status-filter";
import { UserTypeFilter } from "@/components/filter/predefined/user-type-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type User = components["schemas"]["User"];

const DEFAULT_PAGE_SIZE = 100;
const USER_SEARCH_FIELDS = Object.values(PathsV1UserSearchPostParametersQuerySearchField);

const DEFAULT_FILTER_VALUES = {
  departments: [],
  companies: [],
  branches: [],
  brands: [],
  occupations: [],
  jobFunctions: [],
  userTypes: [],
  userStatuses: [],
};

export function UserView() {
  const { t } = useTranslation(["common", "masterdata"]);
  const ability = useAbility();
  const canManage = ability.can(Action.Manage, "User");
  const { getActionsForUser, dialogs } = useUserActions();

  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(DEFAULT_FILTER_VALUES);

  const filters = useMemo(
    () => [
      new DepartmentFilter(),
      new CompanyFilter(),
      new BranchFilter(),
      new BrandFilter(),
      new OccupationFilter(),
      new JobFunctionFilter(),
      new UserTypeFilter(),
      new UserStatusFilter(),
    ],
    [],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: values,
    }));
  }, []);

  const customRowActions = useCallback(
    (row: DataTableRow<User>) =>
      getActionsForUser(row.original).map(({ label, icon: Icon, onClick }) => ({
        label,
        icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
        onClick,
      })),
    [getActionsForUser],
  );

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("common:ManageUsers")}
      subTitle={t("common:SearchFilterAndManageEntriesDescription")}
    >
      <UserTable
        showExportButton
        query={{
          searchField: USER_SEARCH_FIELDS,
        }}
        body={{
          ...(filterValues.departments.length > 0 && {
            departments: filterValues.departments,
          }),
          ...(filterValues.companies.length > 0 && {
            companies: filterValues.companies,
          }),
          ...(filterValues.branches.length > 0 && { mainBranch: filterValues.branches }),
          ...(filterValues.brands.length > 0 && { brands: filterValues.brands }),
          ...(filterValues.occupations.length > 0 && {
            occupations: filterValues.occupations,
          }),
          ...(filterValues.jobFunctions.length > 0 && {
            jobFunctions: filterValues.jobFunctions,
          }),
          ...(filterValues.userTypes.length > 0 && {
            type: filterValues.userTypes as UserType[],
          }),
          ...(filterValues.userStatuses.length > 0 && {
            status: filterValues.userStatuses as UserStatus[],
          }),
        }}
        pageSize={DEFAULT_PAGE_SIZE}
        createButtonText={t("masterdata:InviteGuest")}
        createButtonLink="/invite-guest"
        showCreateButton={canManage}
        searchPlaceholder={t("common:SearchUser")}
        heightMode={DataTableHeightMode.Viewport}
        onRowNavigate={createRowNavigatePath("/masterdata/user")}
        filters={filters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        customRowActions={customRowActions}
      />
      {dialogs}
    </CenteredLayout>
  );
}
